/**
 * VerifyEmailRequiredPage — Phase SEC-A
 * 
 * Shown when REQUIRE_EMAIL_VERIFIED_FOR_APP_ACCESS=true and user.is_verified=false.
 * Blocks access to all other authenticated routes until email is verified.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, RefreshCw, LogOut, CheckCircle, AlertCircle, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL } from 'apiConfig';

const VerifyEmailRequiredPage: React.FC = () => {
  const { user, logout, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(
        `${API_BASE_URL}/auth/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verification email sent! Check your inbox.');
    } catch (error: any) {
      const message = error?.response?.data?.detail || 'Failed to send verification email';
      if (message === 'Email already verified') {
        toast.success('Your email is already verified!');
        await refreshUser();
        navigate('/home');
      } else {
        toast.error(message);
      }
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      await refreshUser();
      // After refresh, check if verified
      // The AuthContext will update, and ProtectedRoute will handle redirect
      toast.info('Checking verification status...');
      // Give a moment for state to update
      setTimeout(() => {
        setChecking(false);
      }, 1000);
    } catch {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="verify-email-required-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-2xl">Verify Your Email</CardTitle>
          <CardDescription className="text-base mt-2">
            Please verify your email address to continue using LitCurrent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email info */}
          <div className="bg-slate-100 rounded-lg p-4 text-center">
            <p className="text-sm text-slate-600">
              We sent a verification link to:
            </p>
            <p className="font-medium text-slate-900 mt-1" data-testid="user-email">
              {user?.email}
            </p>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Click the verification link in your email</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <Inbox className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>Check your spam/junk folder if you don't see it</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-600">
              <RefreshCw className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
              <span>Click "I've verified" below after completing verification</span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handleCheckVerification}
              className="w-full"
              disabled={checking}
              data-testid="check-verification-btn"
            >
              {checking ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  I've Verified My Email
                </>
              )}
            </Button>

            <Button
              onClick={handleResendVerification}
              variant="outline"
              className="w-full"
              disabled={resending}
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Why verify?</p>
                <p className="mt-1">
                  Email verification helps us keep LitCurrent secure and prevents automated abuse.
                  This is separate from professional verification for community posting.
                </p>
              </div>
            </div>
          </div>

          {/* Logout link */}
          <div className="text-center pt-2 border-t">
            <button
              onClick={handleLogout}
              className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="logout-link"
            >
              <LogOut className="h-4 w-4" />
              Sign out and use a different account
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailRequiredPage;
